/* eslint-disable @typescript-eslint/no-var-requires */
import { IFileSystemService } from './IFileSystemService';
import { BinaryResponse } from '@/Api';

const fs = require('fs');
const os = require('os');
const path = require('path');
const mime = require('mime');

const allowedExtensions = /(\.jpg|\.jpeg|\.png|\.gif|\.doc|\.docx|\.rtf|\.xls|\.xlsx|\.xlsm|\.ppt|\.pptx|\.pdf|\.txt)$/i;

export class FileSystemService implements IFileSystemService {
  isPathExists(filePath: string): boolean {
    return fs.existsSync(filePath);
  }

  getTempFolderPath(): string {
    return os.tmpdir();
  }

  getFileName(response: BinaryResponse): string {
    return response.fileName === '' ? Date.now().toString() + '.pdf' : response.fileName.replace(/"/g, '');
  }

  getFileExtension(fileName: string): string {
    return path.extname(fileName);
  }

  saveFile(file: BinaryResponse, folderPath: string): string {
    try {
      this.createFolder(folderPath);

      const fileName = this.getFileName(file);
      const fileExtension = this.getFileExtension(fileName);
      const filePath = path.join(folderPath, fileName);

      let index = 1;
      let replacedPath = filePath;

      while (fs.existsSync(replacedPath)) {
        replacedPath = filePath.replace(fileExtension, ` (${index})${fileExtension}`);
        index++;
      }

      fs.writeFileSync(replacedPath, file.data, 'binary');

      return replacedPath;
    } catch (error) {
      return '';
    }
  }

  deleteFile(filePath: string): boolean {
    try {
      if (fs.existsSync(filePath)) {
        fs.unlinkSync(filePath);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  moveFile(fromPath: string, toPath: string): boolean {
    try {
      fs.renameSync(fromPath, toPath);
      return true;
    } catch (error) {
      return false;
    }
  }

  createFolder(folderPath: string): boolean {
    try {
      if (!fs.existsSync(folderPath)) {
        fs.mkdirSync(folderPath, { recursive: true });
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  readFolder(folderPath: string): string[] {
    const folderFiles: string[] = fs.readdirSync(folderPath);
    // exclude hidden files
    return folderFiles.filter((file) => !file.startsWith('~'));
  }

  deleteFiles(folderPath: string): string[] {
    const fileNames = this.readFolder(folderPath);
    const lockedFileNames: string[] = [];

    fileNames.forEach((fileName) => {
      if (path.extname(fileName) != '' && allowedExtensions.exec(fileName)) {
        const filePath = path.resolve(folderPath, fileName);

        const isSuccess = this.deleteFile(filePath);

        if (!isSuccess) {
          lockedFileNames.push(fileName);
        }
      }
    });

    return lockedFileNames;
  }

  moveFiles(fromPath: string, toPath: string): string[] {
    const fileNames = this.readFolder(fromPath);
    const lockedFileNames: string[] = [];

    this.createFolder(toPath);

    fileNames.forEach((fileName: string) => {
      if (path.extname(fileName) != '' && allowedExtensions.exec(fileName)) {
        const from = path.resolve(fromPath, fileName);
        const to = path.resolve(toPath, fileName);

        const isSuccess = this.moveFile(from, to);

        if (!isSuccess) {
          lockedFileNames.push(fileName);
        }
      }
    });

    return lockedFileNames;
  }

  loadFiles(folderPath: string) {
    const files: File[] = [];
    const fileNames = this.readFolder(folderPath);

    fileNames.forEach((fileName: string) => {
      if (path.extname(fileName) != '' && allowedExtensions.exec(fileName)) {
        const data = fs.readFileSync(path.resolve(folderPath, fileName));
        const blob = new Blob([data]);

        const mimeType = mime.lookup(fileName);
        const type = mimeType.includes('officedocument') ? 'application/octet-stream' : mimeType;

        files.push(new File([blob], fileName, { type }));
      }
    });

    return files;
  }
}
